import React, { useState } from "react";
import { checkRent, describeCheck, rentSol } from "../../lib/rentCheck";
import { logToolEvent } from "../../lib/toolEvents";
import "./rentCheck.css";
import { TokenTag } from "../../solstack/components/TokenLogo";
import { SOL_MINT } from "../../solstack/lib/mintLogos";


/**
 * "How much SOL is stuck in your wallet?" — paste any address, see the rent
 * its token accounts hold, then go to Rent Reclaim to get it back. Reads
 * mainnet only and needs no wallet, so it works on the marketing pages too.
 * `reclaimLink(address, label)` renders the link into the app (an <a> on the
 * marketing site, a router <Link> inside the app). The pasted address is
 * never logged, only what was found.
 */
export const RentCheck = ({ title = "How much SOL is stuck in your wallet?", reclaimLink, className = "" }) => {
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [result, setResult] = useState(null);

  const run = async (e) => {
    e.preventDefault();
    const address = input.trim();
    if (!address) return;
    setBusy(true);
    setError("");
    setResult(null);
    try {
      const found = await checkRent(address, "mainnet");
      setResult({ address, ...found });
      logToolEvent("check", {
        network: "mainnet",
        detail: { empty: found.empty, held: found.held, dust: found.dust, sol: found.emptyLamports / 1e9, from: "home" },
      });
    } catch (err) {
      setError(err.message || "Couldn't check that wallet.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className={`rc-card ${className}`.trim()}>
      <p className="rc-kick">{title}</p>
      <form className="rc-form" onSubmit={run}>
        <input
          className="rc-input"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder="Paste a wallet address"
          aria-label="Solana wallet address"
          spellCheck={false}
          autoComplete="off"
        />
        <button type="submit" className="rc-btn" disabled={busy || !input.trim()}>
          {busy ? "Checking…" : "Check"}
        </button>
      </form>
      {!result && !error && <p className="rc-note">Reads public on-chain data. Nothing to connect or sign.</p>}
      {error && <p className="rc-out rc-err">{error}</p>}
      {result && (result.empty || result.held) ? (
        <div className="rc-out">
          <p className="rc-sol">
            {rentSol(describeCheck(result).lamports)}{" "}
            <small>
              <TokenTag mint={SOL_MINT}>SOL</TokenTag>
            </small>
          </p>
          <p className="rc-what">{describeCheck(result).text}</p>
          {reclaimLink(result.address, "Get it back with Rent Reclaim →")}
        </div>
      ) : null}
      {result && !result.empty && !result.held && (
        <p className="rc-out">Nothing locked: this wallet has no token accounts left open.</p>
      )}
    </div>
  );
};
